import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { translationService } from "../services/core/translationService";
import { routeConfig, findRouteByPath } from "../config/routes";
import { logger } from "../utils/logger";

function uniq<T>(arr: T[]): T[] {
  return Array.from(new Set(arr));
}

function namespacesForGuest(pathname: string): string[] {
  const parts = pathname.split("/").filter(Boolean);
  if (parts[0] !== "g") return ["Common"];

  // online check-in: /g/online-checkin/:token OR /g/:token/online-checkin
  if (parts[1] === "online-checkin" || parts[2] === "online-checkin") {
    return ["Common", "Guest", "Guest.online_checkin", "Guest.general"];
  }

  const sub = parts[2] || "";
  switch (sub) {
    case "menu":
      return ["Common", "Guest", "Guest.menu", "Guest.general", "Guest.payment", "Guest.upselling", "Guest.checkout", "Guest.terms"];
    case "late-checkout":
      return ["Common", "Guest", "Guest.checkout", "Guest.general", "Guest.terms"];
    case "services":
      return ["Common", "Guest", "Guest.services", "Guest.general", "Service", "Guest.terms"];
    case "chat":
    case "ai-chat":
      return ["Common", "Guest", "Guest.chat", "Guest.general", "Guest.menu"];
    case "transfers":
      return ["Common", "Guest", "Guest.transfers", "Guest.general", "Guest.terms"];
    case "door-control":
      return ["Common", "Guest", "Guest.door", "Guest.general"];
    case "activities":
      return ["Common", "Guest", "Guest.activities", "Guest.general", "Guest.terms"];
    case "guide":
      return ["Common", "Guest", "Guest.guide", "Guest.general"];
    case "payment-result":
      return ["Common", "Guest", "Guest.payment", "Guest.general"];
    case "wifi":
      return ["Common", "Guest", "Guest.general"];
    default:
      // Home (/g/:token) - includes Guest.terms for TermsAcceptanceModal and Guest.services for featured service badges
      return ["Common", "Guest", "Guest.home", "Guest.general", "Guest.menu", "Guest.terms", "Guest.services"];
  }
}

function computeNamespaces(pathname: string): string[] {
  // Admin section
  if (pathname.startsWith("/admin")) {
    // Special case: first-setup and onboarding wizard need Admin.onboarding namespace
    if (pathname.startsWith("/admin/first-setup") || pathname === "/admin") {
      return ["Common", "Admin.onboarding", "Admin.general"];
    }

    const current = findRouteByPath(
      pathname,
      routeConfig.admin.routes,
      "/admin",
    );
    if (current?.namespaces?.length) return uniq([...current.namespaces, "Admin", "Admin.search"]);
    return ["Common", "Admin.general", "Admin.nav", "Admin", "Admin.search"];
  }

  // Guest section (/g/:token)
  if (pathname.startsWith("/g/") || pathname.startsWith("/g?")) {
    return namespacesForGuest(pathname);
  }

  // Public routes
  if (pathname === "/qr-code-expired") {
    return ["Common", "Guest.general"];
  }

  // Default
  return ["Common"];
}

const RouteAwareTranslationProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const location = useLocation();
  const { i18n } = useTranslation();

  useEffect(() => {
    const load = async () => {
      // Guard against undefined language during i18n initialization
      if (!i18n.language) {
        return;
      }
      const namespaces = computeNamespaces(location.pathname);
      const list = uniq(namespaces);
      try {
        logger.log(
          "[RouteAwareTranslationProvider] Loading namespaces:",
          i18n.language,
          list,
        );
        await translationService.loadNamespaces(i18n.language, list);
      } catch (err) {
        // Non-blocking; log and proceed
        logger.error(
          "[RouteAwareTranslationProvider] Failed to load namespaces:",
          err,
        );
      }
    };
    load();
  }, [location.pathname, i18n.language]);

  return <>{children}</>;
};

export default RouteAwareTranslationProvider;
